/**
 * Ads Page - Facebook Ads performance dashboard
 *
 * Shows:
 * - Multi-select filters for Campaign, Ad Set, and Ad (page-level, shared across all views)
 * - Hero KPI cards (Spend, Impressions, Reach, Leads) with period comparison + secondary metric strip
 * - Daily ad spend area chart + Conversion Rate by Ad horizontal bar chart (uses page-level filters)
 * - Tabbed view: Performance table | Demographics table (+ chart) | Content (creative performance)
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useDateRange } from "../context/DateRangeContext";
import { branding } from "../config/branding";
import {
  ComposedChart,
  AreaChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// ─── Icons ───────────────────────────────────────────────────────────────────

const SyncIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const XIcon = ({ className = "w-3 h-3" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const TrendUpIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const TrendDownIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
  </svg>
);

const CSVIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const InfoIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ─── Metric Info Popover ──────────────────────────────────────────────────────

function MetricPopover({ description, above = false }: { description: string; above?: boolean }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updateCoords = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const popoverWidth = 256; // w-64
    const left = Math.max(8, Math.min(rect.right - popoverWidth, window.innerWidth - popoverWidth - 8));
    if (above) {
      setCoords({ top: rect.top - 8, left });
    } else {
      setCoords({ top: rect.bottom + 8, left });
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        popoverRef.current && !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateCoords();
    setOpen(!open);
  };

  return (
    <>
      <button
        ref={btnRef}
        onClick={handleClick}
        className="text-muted-foreground/40 hover:text-muted-foreground transition-colors"
        title="How is this calculated?"
      >
        <InfoIcon />
      </button>
      {open && coords && createPortal(
        <div
          ref={popoverRef}
          style={{
            position: "fixed",
            top: above ? undefined : coords.top,
            bottom: above ? window.innerHeight - coords.top : undefined,
            left: coords.left,
            zIndex: 9999,
          }}
          className="w-64 rounded-xl border border-border bg-card shadow-2xl p-3 text-xs text-muted-foreground leading-relaxed"
        >
          {description}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface DailyData {
  date: string;
  impressions: number;
  reach: number;
  spend: number;
  clicks: number;
  linkClicks: number;
  leads: number;
}

interface Totals {
  impressions: number;
  reach: number;
  spend: number;
  clicks: number;
  linkClicks: number;
  leads: number;
  videoViews: number;
  video3sViews: number;
  videoP25Views: number;
  videoP50Views: number;
  videoP100Views: number;
  frequency: number;
  ctr: number;
  cpc: number;
  cpm: number;
  completionRate: number;
  conversionRate: number;
  cpl: number;
}

interface PerformanceRow {
  name: string;
  campaignName: string;
  adsetName: string;
  adName: string;
  impressions: number;
  reach: number;
  spend: number;
  clicks: number;
  linkClicks: number;
  leads: number;
  videoViews: number;
  video3sViews: number;
  videoP25Views: number;
  videoP50Views: number;
  videoP100Views: number;
  frequency: number;
  ctr: number;
  completionRate: number;
  cpc: number;
  cpm: number;
  conversionRate: number;
  cpl: number;
}

interface DemographicsRow {
  age: string;
  gender: string;
  impressions: number;
  reach: number;
  spend: number;
  clicks: number;
  linkClicks: number;
  leads: number;
  ctr: number;
  cpc: number;
  cpl: number;
}

interface Pagination {
  page: number;
  limit: number;
  totalRows: number;
  totalPages: number;
}

interface AdsSummary {
  dailyData: DailyData[];
  totals: Totals;
  rows: PerformanceRow[];
  pagination: Pagination;
}

interface LiveMetrics {
  reach: number; impressions: number; spend: number; clicks: number;
  linkClicks: number; leads: number; frequency: number; ctr: number;
  cpl: number; cpc: number; cpm: number; conversionRate: number;
}

interface CreativePerformanceRow {
  adName: string;
  segment: string;
  fbPage: string;
  spend: number;
  impressions: number;
  reach: number;
  ctr: number;
  linkClicks: number;
  cpm: number;
  leads: number;
  optIns: number;
  applications: number;
  callsBooked: number;
  showed: number;
  closed: number;
  cashCollected: number;
  roas: number;
  costPerLead: number;
  costPerOptin: number;
  optinToApp: number;
  appToBooked: number;
  showRate: number;
  closeRate: number;
}

interface CreativePerformanceSummary {
  totalSpend: number;
  avgCostPerLead: number;
  overallRoas: number;
}

interface CreativePerformanceData {
  summary: CreativePerformanceSummary;
  rows: CreativePerformanceRow[];
}

interface SyncStatus {
  recentSyncs: Array<{ startedAt: string; completedAt?: string }>;
}

type Tab = "performance" | "demographics";
type SortDir = "asc" | "desc";

type ChartMetric = "spend" | "impressions" | "reach" | "leads" | "linkClicks" | "ctr" | "cpc" | "cpm" | "cpl" | "frequency" | "conversionRate";

// ─── Formatters ──────────────────────────────────────────────────────────────

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(value ?? 0);

const formatNumber = (value: number) =>
  new Intl.NumberFormat("en-US").format(value ?? 0);

const formatCompact = (value: number) => {
  const v = value ?? 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return formatNumber(v);
};

const formatPercent = (value: number) => `${(value ?? 0).toFixed(2)}%`;

const CHART_COLORS = branding.colors.chart;

// ─── CSV Export ───────────────────────────────────────────────────────────────

function downloadCSV(filename: string, headers: string[], rows: string[][]) {
  const escape = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const content = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([content], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── MultiSelect Component ──────────────────────────────────────────────────

function MultiSelect({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = search
    ? options.filter((o) => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  const toggle = (value: string) => {
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value]
    );
  };

  const buttonLabel =
    selected.length === 0
      ? `All ${label}s`
      : selected.length === 1
        ? (selected[0]?.length ?? 0) > 20 ? selected[0]?.slice(0, 20) + "..." : selected[0]
        : `${selected.length} ${label}s`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 h-9 rounded-lg border px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)] max-w-[220px] transition-colors ${
          selected.length > 0
            ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]"
            : "border-border bg-card text-foreground"
        }`}
      >
        <span className="truncate">{buttonLabel}</span>
        {selected.length > 0 ? (
          <span
            className="flex-shrink-0 hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
          >
            <XIcon />
          </span>
        ) : (
          <ChevronDownIcon />
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-72 rounded-lg border border-border bg-card shadow-xl">
          <div className="p-2 border-b border-border">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}s...`}
              className="w-full h-8 rounded-md border border-border bg-background px-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
              autoFocus
            />
          </div>
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-border text-xs text-muted-foreground">
            <button className="hover:text-foreground transition-colors" onClick={() => onChange(options)}>Select all</button>
            <button className="hover:text-foreground transition-colors" onClick={() => onChange([])}>Clear</button>
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-4">No results</p>
            ) : (
              filtered.map((option) => (
                <label
                  key={option}
                  className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-md hover:bg-secondary/50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option)}
                    onChange={() => toggle(option)}
                    className="rounded border-border text-[var(--brand-primary)] focus:ring-[var(--brand-ring)] h-3.5 w-3.5"
                  />
                  <span className="truncate">{option}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Hero KPI Card ────────────────────────────────────────────────────────────

function HeroCard({
  label,
  value,
  subtitle,
  accentColor,
  loading,
  delay = 0,
  delta,
  invertDelta,
  description,
  chartActive,
  onChartToggle,
  chartColor,
}: {
  label: string;
  value: string;
  subtitle?: string;
  accentColor: string;
  loading?: boolean;
  delay?: number;
  delta?: number | null;
  invertDelta?: boolean;
  description?: string;
  chartActive?: boolean;
  onChartToggle?: () => void;
  chartColor?: string;
}) {
  if (loading) {
    return (
      <div className="relative overflow-hidden bg-card rounded-2xl border border-border p-6 animate-pulse">
        <div className="h-4 w-20 bg-secondary rounded mb-3" />
        <div className="h-9 w-36 bg-secondary rounded mb-1" />
        <div className="h-3 w-16 bg-secondary/50 rounded" />
      </div>
    );
  }

  const isPositive = delta !== null && delta !== undefined
    ? (invertDelta ? delta < 0 : delta > 0)
    : null;

  const effectiveAccent = chartActive && chartColor ? chartColor : accentColor;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: delay * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={onChartToggle}
      className={`relative overflow-hidden bg-card rounded-2xl border p-6 group transition-all duration-200 ${
        onChartToggle ? "cursor-pointer select-none" : ""
      } ${
        chartActive
          ? "border-[var(--brand-primary)]/50 shadow-md"
          : "border-border hover:border-[var(--brand-primary)]/30"
      }`}
    >
      {/* Top accent line */}
      <div
        className={`absolute top-0 left-0 right-0 h-[2px] transition-opacity ${
          chartActive ? "opacity-100" : "opacity-60 group-hover:opacity-100"
        }`}
        style={{ background: `linear-gradient(90deg, ${effectiveAccent}, transparent)` }}
      />
      {/* Active chart indicator dot */}
      {chartActive && chartColor && (
        <div
          className="absolute bottom-0 left-0 right-0 h-[2px]"
          style={{ backgroundColor: chartColor, opacity: 0.7 }}
        />
      )}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {chartActive && chartColor && (
            <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: chartColor }} />
          )}
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          {onChartToggle && !chartActive && (
            <span className="text-[9px] text-muted-foreground/40 font-normal">(click)</span>
          )}
        </div>
        {description && <MetricPopover description={description} />}
      </div>
      <p className="text-3xl font-bold tracking-tight">{value}</p>
      <div className="flex items-center gap-2 mt-1">
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
        {delta !== null && delta !== undefined && (
          <span
            className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
              isPositive
                ? "text-emerald-500 bg-emerald-500/10"
                : "text-red-400 bg-red-400/10"
            }`}
          >
            {isPositive ? <TrendUpIcon /> : <TrendDownIcon />}
            {Math.abs(delta).toFixed(1)}%
          </span>
        )}
      </div>
    </motion.div>
  );
}

// ─── Compact Metric (inline stat for secondary row) ─────────────────────────

function CompactStat({
  label,
  value,
  loading,
  delay = 0,
  description,
  chartActive,
  onChartToggle,
  chartColor,
}: {
  label: string;
  value: string;
  loading?: boolean;
  delay?: number;
  description?: string;
  chartActive?: boolean;
  onChartToggle?: () => void;
  chartColor?: string;
}) {
  if (loading) {
    return (
      <div className="flex flex-col items-center gap-1 px-4 py-3 animate-pulse">
        <div className="h-3 w-10 bg-secondary rounded" />
        <div className="h-5 w-14 bg-secondary rounded" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: delay * 0.04 }}
      onClick={onChartToggle}
      className={`relative flex flex-col items-center gap-0.5 px-4 py-3 transition-colors ${
        onChartToggle ? "cursor-pointer select-none" : ""
      } ${chartActive ? "bg-[var(--brand-primary)]/5" : onChartToggle ? "hover:bg-secondary/30" : ""}`}
    >
      {/* Bottom color stripe when active */}
      {chartActive && chartColor && (
        <div className="absolute bottom-0 left-2 right-2 h-[2px] rounded-full" style={{ backgroundColor: chartColor }} />
      )}
      <div className="flex items-center gap-1">
        {chartActive && chartColor && (
          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: chartColor }} />
        )}
        <span className={`text-[10px] font-semibold uppercase tracking-widest ${chartActive ? "text-foreground" : "text-muted-foreground"}`}>
          {label}
        </span>
        {description && <MetricPopover description={description} above />}
      </div>
      <span className={`text-sm font-bold ${chartActive ? "text-[var(--brand-primary)]" : ""}`}>{value}</span>
    </motion.div>
  );
}

// ─── Chart Tooltip ───────────────────────────────────────────────────────────

function ChartTooltip({ active, payload, label, formatter }: {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
  formatter?: (value: number) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl shadow-2xl px-4 py-3 text-sm backdrop-blur-sm">
      <p className="font-semibold mb-1.5 text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2.5 py-0.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}</span>
          <span className="font-bold ml-auto">{formatter ? formatter(entry.value) : formatNumber(entry.value)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Empty Chart ─────────────────────────────────────────────────────────────

function EmptyChart({ title }: { title: string }) {
  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-4">{title}</h3>
      <div className="h-72 flex flex-col items-center justify-center text-muted-foreground">
        <svg className="w-10 h-10 mb-3 opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <p className="text-xs">No data available</p>
      </div>
    </div>
  );
}

// ─── Chart Metric Config ──────────────────────────────────────────────────────

const CHART_METRIC_CONFIG: Record<ChartMetric, { label: string; format: (v: number) => string; color: string }> = {
  spend:          { label: "Spend",        format: formatCurrency,              color: CHART_COLORS[0] },
  impressions:    { label: "Impressions",  format: formatNumber,                color: CHART_COLORS[1] },
  reach:          { label: "Reach",        format: formatNumber,                color: CHART_COLORS[2] },
  leads:          { label: "Leads",        format: formatNumber,                color: CHART_COLORS[3] },
  linkClicks:     { label: "Link Clicks",  format: formatNumber,                color: CHART_COLORS[4] },
  ctr:            { label: "CTR",          format: formatPercent,               color: CHART_COLORS[1] },
  cpc:            { label: "CPC",          format: formatCurrency,              color: CHART_COLORS[2] },
  cpm:            { label: "CPM",          format: formatCurrency,              color: CHART_COLORS[3] },
  cpl:            { label: "CPL",          format: formatCurrency,              color: CHART_COLORS[4] },
  frequency:      { label: "Frequency",    format: (v) => v.toFixed(2),        color: CHART_COLORS[0] },
  conversionRate: { label: "Conv. Rate",   format: formatPercent,               color: CHART_COLORS[3] },
};

function normalizeValues(values: number[]): number[] {
  const finite = values.filter(isFinite);
  if (finite.length === 0) return values.map(() => 0);
  const min = Math.min(...finite);
  const max = Math.max(...finite);
  if (max === min) return values.map(() => 500);
  return values.map(v => isFinite(v) ? ((v - min) / (max - min)) * 1000 : 0);
}

function MultiMetricTooltip({
  active,
  payload,
  label,
  activeList,
}: {
  active?: boolean;
  payload?: Array<{ payload: Record<string, number | string> }>;
  label?: string;
  activeList: ChartMetric[];
}) {
  if (!active || !payload?.length) return null;
  const raw = payload[0]?.payload ?? {};
  const dateStr = label
    ? new Date(label).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";
  return (
    <div className="bg-card border border-border rounded-xl shadow-2xl px-4 py-3 backdrop-blur-sm min-w-[175px]">
      <p className="font-semibold mb-2 text-xs text-muted-foreground uppercase tracking-wide">{dateStr}</p>
      {activeList.map(metric => {
        const conf = CHART_METRIC_CONFIG[metric];
        const rawVal = raw[`${metric}_raw`];
        return (
          <div key={metric} className="flex items-center gap-2 py-0.5">
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: conf.color }} />
            <span className="text-muted-foreground text-xs">{conf.label}</span>
            <span className="font-bold ml-auto text-xs pl-3 tabular-nums">
              {typeof rawVal === "number" ? conf.format(rawVal) : "—"}
            </span>
          </div>
        );
      })}
      <p className="text-[10px] text-muted-foreground/40 mt-2 pt-2 border-t border-border">Y-axis normalized · real values shown</p>
    </div>
  );
}

// ─── Spend Chart ─────────────────────────────────────────────────────────────

function SpendChart({ data, loading, activeMetrics }: { data: DailyData[]; loading: boolean; activeMetrics: Set<ChartMetric> }) {
  const activeList = [...activeMetrics] as ChartMetric[];

  if (loading) return <div className="h-80 bg-card rounded-2xl border border-border animate-pulse" />;
  if (data.length === 0) return <EmptyChart title="Daily Performance" />;

  // Enrich daily data with computed metrics
  const enriched = data.map(d => ({
    date: d.date,
    spend: d.spend,
    impressions: d.impressions,
    reach: d.reach,
    leads: d.leads,
    linkClicks: d.linkClicks,
    ctr: d.impressions > 0 ? (d.clicks / d.impressions) * 100 : 0,
    cpc: d.clicks > 0 ? d.spend / d.clicks : 0,
    cpm: d.impressions > 0 ? (d.spend / d.impressions) * 1000 : 0,
    cpl: d.leads > 0 ? d.spend / d.leads : 0,
    frequency: d.reach > 0 ? d.impressions / d.reach : 0,
    conversionRate: d.linkClicks > 0 ? (d.leads / d.linkClicks) * 100 : 0,
  }));

  // Normalize each active metric to 0–1000
  const normalizedByMetric: Record<string, number[]> = {};
  for (const metric of activeList) {
    normalizedByMetric[metric] = normalizeValues(enriched.map(d => d[metric as keyof typeof d] as number));
  }

  // Build chart rows with both raw and normalized values
  const chartData = enriched.map((d, i) => {
    const row: Record<string, number | string> = { date: d.date };
    for (const metric of activeList) {
      row[`${metric}_raw`] = d[metric as keyof typeof d] as number;
      row[`${metric}_norm`] = normalizedByMetric[metric][i];
    }
    return row;
  });

  const spendActive = activeList.includes("spend");

  return (
    <div className="bg-card rounded-2xl border border-border p-6">
      <div className="flex items-start justify-between mb-5 gap-4">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground whitespace-nowrap">Daily Performance</h3>
        {activeList.length > 0 && (
          <div className="flex flex-wrap justify-end gap-x-4 gap-y-1">
            {activeList.map(metric => {
              const conf = CHART_METRIC_CONFIG[metric];
              return (
                <div key={metric} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: conf.color }} />
                  <span className="text-xs text-muted-foreground">{conf.label}</span>
                </div>
              );
            })}
          </div>
        )}
        {activeList.length === 0 && (
          <span className="text-xs text-muted-foreground/60 italic">Click a metric above to show it on the chart</span>
        )}
      </div>
      <div className="h-72">
        {activeList.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <p className="text-xs text-muted-foreground/50">No metrics selected</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <defs>
                {spendActive && (
                  <linearGradient id="spendGradientNorm" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_METRIC_CONFIG.spend.color} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={CHART_METRIC_CONFIG.spend.color} stopOpacity={0} />
                  </linearGradient>
                )}
              </defs>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.08} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                tickFormatter={(v) => new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 1000]}
                ticks={[0, 250, 500, 750, 1000]}
                tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                tickFormatter={(v) => String(v)}
                axisLine={false}
                tickLine={false}
                width={35}
              />
              <Tooltip
                content={(props) => (
                  <MultiMetricTooltip
                    active={props.active}
                    payload={props.payload as Array<{ payload: Record<string, number | string> }>}
                    label={props.label as string}
                    activeList={activeList}
                  />
                )}
              />
              {activeList.map(metric => {
                const conf = CHART_METRIC_CONFIG[metric];
                if (metric === "spend") {
                  return (
                    <Area
                      key={metric}
                      type="monotone"
                      dataKey="spend_norm"
                      name={conf.label}
                      stroke={conf.color}
                      fill="url(#spendGradientNorm)"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4, fill: conf.color }}
                    />
                  );
                }
                return (
                  <Line
                    key={metric}
                    type="monotone"
                    dataKey={`${metric}_norm`}
                    name={conf.label}
                    stroke={conf.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4, fill: conf.color }}
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// ─── Sort Arrow ──────────────────────────────────────────────────────────────

function SortArrow({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <span className="text-muted-foreground/30 ml-1">&#x25B4;&#x25BE;</span>;
  return <span className="ml-1 text-[var(--brand-primary)]">{dir === "asc" ? "▲" : "▼"}</span>;
}

// ─── Performance Table ───────────────────────────────────────────────────────

function PerformanceTable({
  data,
  loading,
  creativeRows,
}: {
  data: PerformanceRow[];
  loading: boolean;
  creativeRows?: CreativePerformanceRow[];
}) {
  const [sortKey, setSortKey] = useState<keyof PerformanceRow>("spend");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (key: keyof PerformanceRow) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sorted = [...data].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  if (loading) {
    return (
      <div className="bg-card rounded-2xl border border-border p-6">
        <div className="h-5 w-32 bg-secondary rounded mb-5 animate-pulse" />
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 bg-secondary/30 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  // Build ROAS lookup by adName from creative performance data
  const roasMap = new Map<string, number>();
  if (creativeRows) {
    for (const r of creativeRows) {
      roasMap.set(r.adName.toLowerCase(), r.roas);
    }
  }

  const columns: Array<{ key: keyof PerformanceRow; label: string; format: (v: number) => string }> = [
    { key: "campaignName", label: "Campaign", format: () => "" },
    { key: "adsetName", label: "Ad Set", format: () => "" },
    { key: "impressions", label: "Impressions", format: formatNumber },
    { key: "reach", label: "Reach", format: formatNumber },
    { key: "clicks", label: "Clicks", format: formatNumber },
    { key: "leads", label: "Leads", format: formatNumber },
    { key: "videoViews", label: "Video Views", format: formatNumber },
    { key: "video3sViews", label: "3s Views", format: formatNumber },
    { key: "videoP25Views", label: "Video 25%", format: formatNumber },
    { key: "videoP50Views", label: "Video 50%", format: formatNumber },
    { key: "videoP100Views", label: "Video 100%", format: formatNumber },
    { key: "linkClicks", label: "Link Clicks", format: formatNumber },
    { key: "spend", label: "Spend", format: formatCurrency },
    { key: "frequency", label: "Frequency", format: (v) => (v ?? 0).toFixed(2) },
    { key: "ctr", label: "CTR", format: formatPercent },
    { key: "completionRate", label: "Completion %", format: formatPercent },
    { key: "cpc", label: "CPC", format: formatCurrency },
    { key: "cpm", label: "CPM", format: formatCurrency },
    { key: "conversionRate", label: "Conv. Rate", format: formatPercent },
    { key: "cpl", label: "CPL", format: formatCurrency },
  ];

  const stringCols = new Set(["campaignName", "adsetName"]);

  // Compute totals row
  const totals: Partial<PerformanceRow> = {
    impressions: data.reduce((s, r) => s + r.impressions, 0),
    reach: data.reduce((s, r) => s + r.reach, 0),
    spend: data.reduce((s, r) => s + r.spend, 0),
    clicks: data.reduce((s, r) => s + r.clicks, 0),
    linkClicks: data.reduce((s, r) => s + r.linkClicks, 0),
    leads: data.reduce((s, r) => s + r.leads, 0),
    videoViews: data.reduce((s, r) => s + r.videoViews, 0),
    video3sViews: data.reduce((s, r) => s + r.video3sViews, 0),
    videoP25Views: data.reduce((s, r) => s + r.videoP25Views, 0),
    videoP50Views: data.reduce((s, r) => s + r.videoP50Views, 0),
    videoP100Views: data.reduce((s, r) => s + r.videoP100Views, 0),
  };
  const totalImpressions = totals.impressions ?? 0;
  const totalClicks = totals.clicks ?? 0;
  const totalSpend = totals.spend ?? 0;
  const totalLeads = totals.leads ?? 0;
  const totalReach = totals.reach ?? 0;
  const total3sViews = totals.video3sViews ?? 0;
  const totalP100Views = totals.videoP100Views ?? 0;
  totals.frequency = totalReach > 0 ? totalImpressions / totalReach : 0;
  totals.ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;
  totals.completionRate = total3sViews > 0 ? (totalP100Views / total3sViews) * 100 : 0;
  totals.cpc = totalClicks > 0 ? totalSpend / totalClicks : 0;
  totals.cpm = totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0;
  totals.conversionRate = totalClicks > 0 ? (totalLeads / totalClicks) * 100 : 0;
  totals.cpl = totalLeads > 0 ? totalSpend / totalLeads : 0;

  const handleExportCSV = () => {
    const headers = ["Ad", "Campaign", "Ad Set", "Impressions", "Reach", "Clicks", "Leads", "Video Views", "3s Views", "Video 25%", "Video 50%", "Video 100%", "Link Clicks", "Spend", "ROAS", "Frequency", "CTR", "Completion %", "CPC", "CPM", "Conv. Rate", "CPL"];
    const rows = sorted.map((r) => {
      const roas = roasMap.get(r.name.toLowerCase());
      return [
      r.name, r.campaignName, r.adsetName,
      String(r.impressions), String(r.reach), String(r.clicks), String(r.leads),
      String(r.videoViews), String(r.video3sViews), String(r.videoP25Views), String(r.videoP50Views), String(r.videoP100Views),
      String(r.linkClicks), formatCurrency(r.spend), roas !== undefined ? `${roas.toFixed(2)}x` : "—",
      r.frequency.toFixed(2),
      formatPercent(r.ctr), formatPercent(r.completionRate), formatCurrency(r.cpc),
      formatCurrency(r.cpm), formatPercent(r.conversionRate), formatCurrency(r.cpl),
      ];
    });
    downloadCSV("ads-performance.csv", headers, rows);
  };

  return (
    <div className="bg-card rounded-2xl border border-border overflow-x-auto">
      <div className="p-6 pb-4 flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Performance</h3>
        {data.length > 0 && (
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
          >
            <CSVIcon />
            Export CSV
          </button>
        )}
      </div>
      {data.length === 0 ? (
        <p className="text-muted-foreground text-center py-10 px-6 text-sm">No performance data for this period</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left">
              <th
                className="pb-3 pl-6 pr-4 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap text-xs"
                onClick={() => handleSort("name")}
              >
                Ad
                <SortArrow active={sortKey === "name"} dir={sortDir} />
              </th>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`pb-3 px-3 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap text-xs ${
                    stringCols.has(col.key) ? "text-left" : "text-right"
                  }`}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                  <SortArrow active={sortKey === col.key} dir={sortDir} />
                </th>
              ))}
              {creativeRows && (
                <th className="pb-3 px-3 font-medium text-muted-foreground whitespace-nowrap text-xs text-right">
                  ROAS
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => (
              <tr key={i} className="border-b border-border/40 hover:bg-secondary/20 transition-colors">
                <td className="py-3.5 pl-6 pr-4 max-w-[200px] truncate font-medium" title={row.name}>
                  {row.name}
                </td>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`py-3.5 px-3 whitespace-nowrap ${
                      stringCols.has(col.key) ? "text-left max-w-[150px] truncate" : "text-right tabular-nums"
                    }`}
                    title={stringCols.has(col.key) ? String(row[col.key]) : undefined}
                  >
                    {stringCols.has(col.key)
                      ? String(row[col.key])
                      : col.format(row[col.key] as number)}
                  </td>
                ))}
                {creativeRows && (() => {
                  const roas = roasMap.get(row.name.toLowerCase());
                  return (
                    <td className="py-3.5 px-3 whitespace-nowrap text-right tabular-nums font-medium text-[var(--brand-primary)]">
                      {roas !== undefined && roas > 0 ? `${roas.toFixed(2)}x` : "—"}
                    </td>
                  );
                })()}
              </tr>
            ))}
            {/* Totals row */}
            <tr className="border-t-2 border-border bg-secondary/10 font-semibold">
              <td className="py-3.5 pl-6 pr-4 text-xs uppercase tracking-wide text-muted-foreground">
                Totals ({data.length} ads)
              </td>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`py-3.5 px-3 whitespace-nowrap text-xs ${
                    stringCols.has(col.key) ? "text-left" : "text-right tabular-nums"
                  }`}
                >
                  {stringCols.has(col.key)
                    ? "—"
                    : col.format((totals[col.key] as number) ?? 0)}
                </td>
              ))}
              {creativeRows && (() => {
                const totalCash = creativeRows.reduce((s, r) => s + r.cashCollected, 0);
                const totalSpend = totals.spend ?? 0;
                const overallRoas = totalSpend > 0 ? totalCash / totalSpend : 0;
                return (
                  <td className="py-3.5 px-3 whitespace-nowrap text-xs text-right tabular-nums font-semibold text-[var(--brand-primary)]">
                    {overallRoas > 0 ? `${overallRoas.toFixed(2)}x` : "—"}
                  </td>
                );
              })()}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Demographics Table + Chart ──────────────────────────────────────────────

type DemoMetric = "leads" | "spend" | "impressions";

function DemographicsTable({
  data,
  loading,
  overallRoas,
  cashCollected,
}: {
  data: DemographicsRow[];
  loading: boolean;
  overallRoas?: number;
  cashCollected?: number;
}) {
  const [sortKey, setSortKey] = useState<keyof DemographicsRow>("spend");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [chartMetric, setChartMetric] = useState<DemoMetric>("leads");

  const handleSort = (key: keyof DemographicsRow) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sorted = [...data].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="bg-card rounded-2xl border border-border p-6 h-72 animate-pulse" />
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="h-5 w-48 bg-secondary rounded mb-5 animate-pulse" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-secondary/30 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const columns: Array<{ key: keyof DemographicsRow; label: string; format: (v: number | string) => string }> = [
    { key: "impressions", label: "Impressions", format: (v) => formatNumber(v as number) },
    { key: "reach", label: "Reach", format: (v) => formatNumber(v as number) },
    { key: "clicks", label: "Clicks", format: (v) => formatNumber(v as number) },
    { key: "leads", label: "Leads", format: (v) => formatNumber(v as number) },
    { key: "linkClicks", label: "Link Clicks", format: (v) => formatNumber(v as number) },
    { key: "spend", label: "Spend", format: (v) => formatCurrency(v as number) },
    { key: "ctr", label: "CTR", format: (v) => formatPercent(v as number) },
    { key: "cpc", label: "CPC", format: (v) => formatCurrency(v as number) },
    { key: "cpl", label: "CPL", format: (v) => formatCurrency(v as number) },
  ];

  // Build stacked bar chart data grouped by age
  const ageGroups = [...new Set(data.map((r) => r.age))].sort();
  const genders = [...new Set(data.map((r) => {
    const g = r.gender.toLowerCase();
    return g.charAt(0).toUpperCase() + g.slice(1);
  }))].sort();

  const chartData = ageGroups.map((age) => {
    const row: Record<string, number | string> = { age };
    for (const g of genders) {
      const match = data.find((r) => r.age === age && r.gender.toLowerCase() === g.toLowerCase());
      row[g] = match ? match[chartMetric] : 0;
    }
    return row;
  });

  const metricFormatter = chartMetric === "spend" ? formatCurrency : formatNumber;
  const metricLabel = chartMetric === "leads" ? "Leads" : chartMetric === "spend" ? "Spend" : "Impressions";

  const handleExportCSV = () => {
    const headers = ["Age", "Gender", "Impressions", "Reach", "Clicks", "Leads", "Link Clicks", "Spend", "CTR", "CPC", "CPL"];
    const rows = sorted.map((r) => [
      r.age, r.gender,
      String(r.impressions), String(r.reach), String(r.clicks), String(r.leads),
      String(r.linkClicks), formatCurrency(r.spend), formatPercent(r.ctr),
      formatCurrency(r.cpc), formatCurrency(r.cpl),
    ]);
    downloadCSV("ads-demographics.csv", headers, rows);
  };

  const genderColors: Record<string, string> = {
    Female: CHART_COLORS[0],
    Male: CHART_COLORS[1],
    Unknown: CHART_COLORS[2],
  };

  const demoTotalSpend = data.reduce((s, r) => s + r.spend, 0);

  return (
    <div className="space-y-6">
      {/* ROAS Summary Strip */}
      {(overallRoas !== undefined || cashCollected !== undefined) && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card rounded-2xl border border-border p-5 flex flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Overall ROAS</span>
              <MetricPopover
                description="Return on Ad Spend = Cash Collected ÷ Ad Spend. Attribution is all-time via: ad name → utm_content → opt-in emails → EOC records. Demographic-level ROAS breakdown is not available from Meta's API."
                above={false}
              />
            </div>
            <span className="text-2xl font-bold text-[var(--brand-primary)]">
              {overallRoas !== undefined ? `${overallRoas.toFixed(2)}x` : "—"}
            </span>
            <span className="text-xs text-muted-foreground">from all active creatives</span>
          </div>
          <div className="bg-card rounded-2xl border border-border p-5 flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Cash Collected</span>
            <span className="text-2xl font-bold text-foreground">
              {cashCollected !== undefined ? formatCurrency(cashCollected) : "—"}
            </span>
            <span className="text-xs text-muted-foreground">all-time via EOC attribution</span>
          </div>
          <div className="bg-card rounded-2xl border border-border p-5 flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Demo Spend</span>
            <span className="text-2xl font-bold text-foreground">
              {formatCurrency(demoTotalSpend)}
            </span>
            <span className="text-xs text-muted-foreground">in selected date range</span>
          </div>
        </div>
      )}

      {/* Demographics Chart */}
      {data.length > 0 && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Demographics by Age & Gender
            </h3>
            <div className="flex gap-1 bg-secondary/30 rounded-lg p-1">
              {(["leads", "spend", "impressions"] as DemoMetric[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setChartMetric(m)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-all capitalize ${
                    chartMetric === m
                      ? "bg-[var(--brand-primary)] text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.08} />
                <XAxis
                  dataKey="age"
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "var(--muted-foreground)" }}
                  tickFormatter={(v: number) => chartMetric === "spend" ? `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}` : formatCompact(v)}
                  axisLine={false}
                  tickLine={false}
                  width={50}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-card border border-border rounded-xl shadow-2xl px-4 py-3 text-sm backdrop-blur-sm">
                        <p className="font-semibold mb-1.5 text-xs text-muted-foreground uppercase tracking-wide">{label}</p>
                        {payload.map((entry, i) => (
                          <div key={i} className="flex items-center gap-2.5 py-0.5">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
                            <span className="text-muted-foreground">{entry.name}</span>
                            <span className="font-bold ml-auto">{metricFormatter(entry.value as number)}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }}
                />
                <Legend
                  formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
                />
                {genders.map((g) => (
                  <Bar
                    key={g}
                    dataKey={g}
                    name={`${g} · ${metricLabel}`}
                    fill={genderColors[g] ?? CHART_COLORS[3]}
                    radius={[3, 3, 0, 0]}
                    stackId="a"
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Demographics Table */}
      <div className="bg-card rounded-2xl border border-border overflow-x-auto">
        <div className="p-6 pb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Demographics Breakdown</h3>
          {data.length > 0 && (
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
            >
              <CSVIcon />
              Export CSV
            </button>
          )}
        </div>
        {data.length === 0 ? (
          <p className="text-muted-foreground text-center py-10 px-6 text-sm">No demographics data for this period</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th
                  className="pb-3 pl-6 pr-4 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap text-xs"
                  onClick={() => handleSort("age")}
                >
                  Age
                  <SortArrow active={sortKey === "age"} dir={sortDir} />
                </th>
                <th
                  className="pb-3 px-3 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap text-xs"
                  onClick={() => handleSort("gender")}
                >
                  Gender
                  <SortArrow active={sortKey === "gender"} dir={sortDir} />
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="pb-3 px-3 font-medium text-muted-foreground text-right cursor-pointer select-none whitespace-nowrap text-xs"
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    <SortArrow active={sortKey === col.key} dir={sortDir} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i} className="border-b border-border/40 hover:bg-secondary/20 transition-colors">
                  <td className="py-3.5 pl-6 pr-4">{row.age}</td>
                  <td className="py-3.5 px-3 capitalize">{row.gender}</td>
                  {columns.map((col) => (
                    <td key={col.key} className="py-3.5 px-3 text-right whitespace-nowrap tabular-nums">
                      {col.format(row[col.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Creative Performance Table ──────────────────────────────────────────────

function CreativePerformanceTable({
  data,
  loading,
  campaignOptions,
  adsetOptions,
}: {
  data: CreativePerformanceData | null;
  loading: boolean;
  campaignOptions: string[];
  adsetOptions: string[];
}) {
  const [sortKey, setSortKey] = useState<keyof CreativePerformanceRow>("spend");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filterSegment, setFilterSegment] = useState<string>("");
  const [filterCampaign, setFilterCampaign] = useState<string[]>([]);

  const handleSort = (key: keyof CreativePerformanceRow) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const filteredRows = (data?.rows ?? []).filter((r) => {
    if (filterSegment && r.segment !== filterSegment) return false;
    if (filterCampaign.length > 0) {
      // We don't have campaignName in creative rows, but we can filter by segment
    }
    return true;
  });

  const sorted = [...filteredRows].sort((a, b) => {
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    return sortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-card rounded-2xl border border-border p-6 animate-pulse">
              <div className="h-4 w-24 bg-secondary rounded mb-3" />
              <div className="h-8 w-32 bg-secondary rounded" />
            </div>
          ))}
        </div>
        <div className="bg-card rounded-2xl border border-border p-6">
          <div className="h-5 w-32 bg-secondary rounded mb-5 animate-pulse" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-secondary/30 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const columns: Array<{
    key: keyof CreativePerformanceRow;
    label: string;
    format: (v: number | string) => string;
  }> = [
    { key: "segment", label: "Segment", format: (v) => String(v) },
    { key: "fbPage", label: "IG Page", format: (v) => String(v) },
    { key: "spend", label: "Spend", format: (v) => formatCurrency(v as number) },
    { key: "impressions", label: "Impr.", format: (v) => formatNumber(v as number) },
    { key: "reach", label: "Reach", format: (v) => formatNumber(v as number) },
    { key: "ctr", label: "CTR", format: (v) => formatPercent(v as number) },
    { key: "linkClicks", label: "Link Clicks", format: (v) => formatNumber(v as number) },
    { key: "cpm", label: "CPM", format: (v) => formatCurrency(v as number) },
    { key: "leads", label: "FB Leads", format: (v) => formatNumber(v as number) },
    { key: "optIns", label: "Opt-Ins", format: (v) => formatNumber(v as number) },
    { key: "applications", label: "Apps", format: (v) => formatNumber(v as number) },
    { key: "callsBooked", label: "Booked", format: (v) => formatNumber(v as number) },
    { key: "showed", label: "Showed", format: (v) => formatNumber(v as number) },
    { key: "closed", label: "Closed", format: (v) => formatNumber(v as number) },
    { key: "cashCollected", label: "Cash Collected", format: (v) => formatCurrency(v as number) },
    { key: "roas", label: "ROAS", format: (v) => `${(v as number).toFixed(2)}x` },
    { key: "costPerLead", label: "CPL", format: (v) => formatCurrency(v as number) },
    { key: "costPerOptin", label: "Cost/Opt-In", format: (v) => formatCurrency(v as number) },
    { key: "optinToApp", label: "Opt→App %", format: (v) => formatPercent(v as number) },
    { key: "appToBooked", label: "App→Book %", format: (v) => formatPercent(v as number) },
    { key: "showRate", label: "Show %", format: (v) => formatPercent(v as number) },
    { key: "closeRate", label: "Close %", format: (v) => formatPercent(v as number) },
  ];

  const stringCols = new Set(["segment", "fbPage"]);

  const handleExportCSV = () => {
    const headers = ["Creative", "Segment", "IG Page", "Spend", "Impressions", "Reach", "CTR", "Link Clicks", "CPM", "FB Leads", "Opt-Ins", "Apps", "Booked", "Showed", "Closed", "Cash Collected", "ROAS", "CPL", "Cost/Opt-In", "Opt→App %", "App→Book %", "Show %", "Close %"];
    const rows = sorted.map((r) => [
      r.adName, r.segment, r.fbPage, formatCurrency(r.spend),
      String(r.impressions), String(r.reach), formatPercent(r.ctr),
      String(r.linkClicks), formatCurrency(r.cpm), String(r.leads),
      String(r.optIns), String(r.applications), String(r.callsBooked),
      String(r.showed), String(r.closed), formatCurrency(r.cashCollected),
      `${r.roas.toFixed(2)}x`, formatCurrency(r.costPerLead),
      formatCurrency(r.costPerOptin), formatPercent(r.optinToApp),
      formatPercent(r.appToBooked), formatPercent(r.showRate), formatPercent(r.closeRate),
    ]);
    downloadCSV("ads-creative-performance.csv", headers, rows);
  };

  // Recompute summary from visible (filtered) rows
  const visibleSpend = filteredRows.reduce((s, r) => s + r.spend, 0);
  const visibleLeads = filteredRows.reduce((s, r) => s + r.leads, 0);
  const visibleCash = filteredRows.reduce((s, r) => s + r.cashCollected, 0);
  const visibleSummary = {
    totalSpend: visibleSpend,
    avgCostPerLead: visibleLeads > 0 ? visibleSpend / visibleLeads : 0,
    overallRoas: visibleSpend > 0 ? visibleCash / visibleSpend : 0,
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-card rounded-2xl border border-border p-6"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Total Ad Spend</p>
            <p className="text-3xl font-bold tracking-tight">{formatCurrency(visibleSummary.totalSpend)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {filteredRows.filter(r => r.spend > 0).length} active creatives
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
            className="bg-card rounded-2xl border border-border p-6"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Avg Cost Per Lead</p>
            <p className="text-3xl font-bold tracking-tight">{formatCurrency(visibleSummary.avgCostPerLead)}</p>
            <p className="text-xs text-muted-foreground mt-1">Across visible creatives</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="bg-card rounded-2xl border border-border p-6"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Overall ROAS</p>
            <p className="text-3xl font-bold tracking-tight text-[var(--brand-primary)]">
              {visibleSummary.overallRoas.toFixed(2)}x
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {formatCurrency(visibleCash)} cash collected
            </p>
          </motion.div>
        </div>
      )}

      {/* Table */}
      <div className="bg-card rounded-2xl border border-border overflow-x-auto">
        <div className="p-6 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Per-Creative Performance
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                Full funnel attribution: ad → opt-in → application → call → close
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Segment filter */}
              <select
                value={filterSegment}
                onChange={(e) => setFilterSegment(e.target.value)}
                className="h-8 rounded-lg border border-border bg-card px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-[var(--brand-ring)]"
              >
                <option value="">All Segments</option>
                <option value="Cold">Cold</option>
                <option value="Warm">Warm</option>
              </select>
              {sorted.length > 0 && (
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors"
                >
                  <CSVIcon />
                  Export CSV
                </button>
              )}
            </div>
          </div>
        </div>
        {sorted.length === 0 ? (
          <p className="text-muted-foreground text-center py-10 px-6 text-sm">
            No creative performance data for this period
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th
                  className="pb-3 pl-6 pr-4 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap text-xs sticky left-0 bg-card"
                  onClick={() => handleSort("adName")}
                >
                  Creative
                  <SortArrow active={sortKey === "adName"} dir={sortDir} />
                </th>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`pb-3 px-3 font-medium text-muted-foreground cursor-pointer select-none whitespace-nowrap text-xs ${
                      stringCols.has(col.key) ? "text-left" : "text-right"
                    }`}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    <SortArrow active={sortKey === col.key} dir={sortDir} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i} className="border-b border-border/40 hover:bg-secondary/20 transition-colors">
                  <td className="py-3.5 pl-6 pr-4 max-w-[200px] truncate font-medium sticky left-0 bg-card" title={row.adName}>
                    {row.adName}
                  </td>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`py-3.5 px-3 whitespace-nowrap ${
                        stringCols.has(col.key) ? "text-left" : "text-right tabular-nums"
                      } ${
                        col.key === "roas" && (row[col.key] as number) > 1
                          ? "text-[var(--brand-primary)] font-semibold"
                          : ""
                      }`}
                    >
                      {stringCols.has(col.key)
                        ? String(row[col.key])
                        : col.format(row[col.key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Backfill Confirm Modal ───────────────────────────────────────────────────

function BackfillModal({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="bg-card border border-border rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4"
      >
        <h2 className="text-lg font-bold mb-2">Backfill Facebook Ads Data</h2>
        <p className="text-sm text-muted-foreground mb-6">
          This will fetch all Facebook Ads data from Jan 1, 2026 to today. The process runs in the background and may take several minutes to complete.
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary/50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white text-sm font-semibold hover:opacity-90 transition-opacity shadow-lg"
          >
            Start Backfill
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Main Ads Page ───────────────────────────────────────────────────────────

export function Ads() {
  const { formatForApi } = useDateRange();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>("performance");
  const [activeChartMetrics, setActiveChartMetrics] = useState<Set<ChartMetric>>(
    () => new Set<ChartMetric>(["spend", "linkClicks"])
  );
  const toggleChartMetric = useCallback((metric: ChartMetric) => {
    setActiveChartMetrics(prev => {
      const next = new Set(prev);
      if (next.has(metric)) next.delete(metric);
      else next.add(metric);
      return next;
    });
  }, []);
  const [summary, setSummary] = useState<AdsSummary | null>(null);
  const [prevTotals, setPrevTotals] = useState<Totals | null>(null);
  const [demographics, setDemographics] = useState<DemographicsRow[]>([]);
  const [demoLoading, setDemoLoading] = useState(false);
  const [liveMetrics, setLiveMetrics] = useState<LiveMetrics | null>(null);
  const [reachLoading, setReachLoading] = useState(true);
  const [creativePerformance, setCreativePerformance] = useState<CreativePerformanceData | null>(null);
  const [creativeLoading, setCreativeLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState<string | null>(null);

  // Filter options
  const [campaignOptions, setCampaignOptions] = useState<string[]>([]);
  const [adsetOptions, setAdsetOptions] = useState<string[]>([]);
  const [adOptions, setAdOptions] = useState<string[]>([]);

  // Selected filters
  const [selectedCampaigns, setSelectedCampaigns] = useState<string[]>([]);
  const [selectedAdsets, setSelectedAdsets] = useState<string[]>([]);
  const [selectedAds, setSelectedAds] = useState<string[]>([]);

  const appendArrayParams = useCallback((params: URLSearchParams, key: string, values: string[]) => {
    for (const v of values) params.append(key, v);
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(`/api/ads/campaigns?${params}`);
      const json = await res.json();
      if (json.success) setCampaignOptions(json.data);
    } catch (err) {
      console.error("Error fetching campaigns:", err);
    }
  }, [formatForApi]);

  const fetchAdsets = useCallback(async () => {
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      for (const c of selectedCampaigns) params.append("campaigns", c);
      const res = await fetch(`/api/ads/adsets?${params}`);
      const json = await res.json();
      if (json.success) {
        setAdsetOptions(json.data);
        setSelectedAdsets((prev) => prev.filter((s) => json.data.includes(s)));
      }
    } catch (err) {
      console.error("Error fetching adsets:", err);
    }
  }, [formatForApi, selectedCampaigns]);

  const fetchAds = useCallback(async () => {
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      for (const c of selectedCampaigns) params.append("campaigns", c);
      for (const a of selectedAdsets) params.append("adsets", a);
      const res = await fetch(`/api/ads/adnames?${params}`);
      const json = await res.json();
      if (json.success) {
        setAdOptions(json.data);
        setSelectedAds((prev) => prev.filter((s) => json.data.includes(s)));
      }
    } catch (err) {
      console.error("Error fetching ad names:", err);
    }
  }, [formatForApi, selectedCampaigns, selectedAdsets]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      appendArrayParams(params, "campaigns", selectedCampaigns);
      appendArrayParams(params, "adsets", selectedAdsets);
      appendArrayParams(params, "ads", selectedAds);

      const res = await fetch(`/api/ads/summary?${params}`);
      const json = await res.json();
      if (json.success) setSummary(json.data);
    } catch (err) {
      console.error("Error fetching ads summary:", err);
    } finally {
      setLoading(false);
    }
  }, [formatForApi, selectedCampaigns, selectedAdsets, selectedAds, appendArrayParams]);

  const fetchPrevTotals = useCallback(async () => {
    try {
      const { startDate, endDate } = formatForApi();
      const start = new Date(startDate);
      const end = new Date(endDate);
      const durationMs = end.getTime() - start.getTime();
      const prevEnd = new Date(start.getTime() - 86400000);
      const prevStart = new Date(prevEnd.getTime() - durationMs);
      const params = new URLSearchParams({
        startDate: prevStart.toISOString().split("T")[0],
        endDate: prevEnd.toISOString().split("T")[0],
      });
      appendArrayParams(params, "campaigns", selectedCampaigns);
      appendArrayParams(params, "adsets", selectedAdsets);
      appendArrayParams(params, "ads", selectedAds);
      const res = await fetch(`/api/ads/summary?${params}`);
      const json = await res.json();
      if (json.success) setPrevTotals(json.data.totals);
    } catch {
      // silent - prev period data is non-critical
    }
  }, [formatForApi, selectedCampaigns, selectedAdsets, selectedAds, appendArrayParams]);

  const fetchDemographics = useCallback(async () => {
    setDemoLoading(true);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      appendArrayParams(params, "campaigns", selectedCampaigns);
      appendArrayParams(params, "adsets", selectedAdsets);

      const res = await fetch(`/api/ads/demographics?${params}`);
      const json = await res.json();
      if (json.success) setDemographics(json.data);
    } catch (err) {
      console.error("Error fetching demographics:", err);
    } finally {
      setDemoLoading(false);
    }
  }, [formatForApi, selectedCampaigns, selectedAdsets, appendArrayParams]);

  const fetchReach = useCallback(async () => {
    setReachLoading(true);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      appendArrayParams(params, "campaigns", selectedCampaigns);
      appendArrayParams(params, "adsets", selectedAdsets);
      appendArrayParams(params, "ads", selectedAds);

      const res = await fetch(`/api/ads/reach?${params}`);
      const json = await res.json();
      if (json.success) {
        setLiveMetrics(json.data);
      }
    } catch (err) {
      console.error("Error fetching live reach:", err);
    } finally {
      setReachLoading(false);
    }
  }, [formatForApi, selectedCampaigns, selectedAdsets, selectedAds, appendArrayParams]);

  const fetchCreativePerformance = useCallback(async () => {
    setCreativeLoading(true);
    try {
      const { startDate, endDate } = formatForApi();
      const params = new URLSearchParams({ startDate, endDate });
      appendArrayParams(params, "campaigns", selectedCampaigns);
      appendArrayParams(params, "adsets", selectedAdsets);
      appendArrayParams(params, "ads", selectedAds);

      const res = await fetch(`/api/ads/creative-performance?${params}`);
      const json = await res.json();
      if (json.success) {
        setCreativePerformance(json.data);
      }
    } catch (err) {
      console.error("Error fetching creative performance:", err);
    } finally {
      setCreativeLoading(false);
    }
  }, [formatForApi, selectedCampaigns, selectedAdsets, selectedAds, appendArrayParams]);

  const fetchLastSynced = useCallback(async () => {
    try {
      const res = await fetch("/api/facebook/sync-status");
      const json = await res.json() as { success: boolean; data: SyncStatus };
      if (json.success && json.data.recentSyncs?.length > 0) {
        setLastSynced(json.data.recentSyncs[0].startedAt);
      }
    } catch {
      // silent
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await fetch("/api/facebook/sync", { method: "POST" });
      await fetchCampaigns();
      await fetchSummary();
      await fetchReach();
      await fetchPrevTotals();
      await fetchCreativePerformance();
      await fetchLastSynced();
      if (activeTab === "demographics") await fetchDemographics();
    } catch (err) {
      console.error("Sync failed:", err);
    } finally {
      setSyncing(false);
    }
  };


  useEffect(() => { fetchCampaigns(); fetchLastSynced(); }, [fetchCampaigns, fetchLastSynced]);
  useEffect(() => { fetchAdsets(); }, [fetchAdsets]);
  useEffect(() => { fetchAds(); }, [fetchAds]);
  useEffect(() => { fetchSummary(); fetchPrevTotals(); }, [fetchSummary, fetchPrevTotals]);
  useEffect(() => { fetchReach(); }, [fetchReach]);
  // Always fetch creative performance (needed for ROAS hero card)
  useEffect(() => { fetchCreativePerformance(); }, [fetchCreativePerformance]);
  useEffect(() => {
    if (activeTab === "demographics") fetchDemographics();
  }, [activeTab, fetchDemographics]);

  const m = liveMetrics;
  const getColor = (i: number) => CHART_COLORS[i] ?? branding.colors.primary;

  const calcDelta = (current: number, prev: number) => {
    if (!prev) return null;
    return ((current - prev) / prev) * 100;
  };

  const tabs: Array<{ value: Tab; label: string }> = [
    { value: "performance", label: "Performance" },
    { value: "demographics", label: "Demographics" },
  ];

  const formatLastSynced = (iso: string) => {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  return (
    <div className="space-y-8">

      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-5">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight">Ads</h1>
            <p className="text-muted-foreground text-sm mt-1">Facebook Ads performance analytics</p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <div className="flex items-center gap-2">
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[var(--brand-gradient-start)] to-[var(--brand-gradient-end)] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 shadow-lg shadow-[var(--brand-primary)]/20"
              >
                <span className={syncing ? "animate-spin" : ""}><SyncIcon /></span>
                {syncing ? "Syncing..." : "Sync Now"}
              </button>
            </div>
            {lastSynced && (
              <p className="text-xs text-muted-foreground">Last synced: {formatLastSynced(lastSynced)}</p>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <MultiSelect label="Campaign" options={campaignOptions} selected={selectedCampaigns} onChange={setSelectedCampaigns} />
          <MultiSelect label="Ad Set" options={adsetOptions} selected={selectedAdsets} onChange={setSelectedAdsets} />
          <MultiSelect label="Ad" options={adOptions} selected={selectedAds} onChange={setSelectedAds} />
          {(selectedCampaigns.length > 0 || selectedAdsets.length > 0 || selectedAds.length > 0) && (
            <button
              onClick={() => { setSelectedCampaigns([]); setSelectedAdsets([]); setSelectedAds([]); }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* ── Hero KPI Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <HeroCard
          label="ROAS"
          value={creativePerformance ? `${creativePerformance.summary.overallRoas.toFixed(2)}x` : "—"}
          subtitle={creativePerformance ? `${formatCurrency(creativePerformance.rows.reduce((s, r) => s + r.cashCollected, 0))} collected` : undefined}
          accentColor={branding.colors.accent}
          loading={creativeLoading}
          delay={0}
          description="Return on Ad Spend = Cash Collected ÷ Ad Spend. Ad spend is filtered to the selected date range. Cash collected is traced all-time via: ad name → utm_content mapping → opt-in emails → EOC (End of Call) records. If an ad has no utm_content mapping it shows 0."
        />
        <HeroCard
          label="Total Spend"
          value={m ? formatCurrency(m.spend) : "$0.00"}
          accentColor={getColor(0)}
          loading={reachLoading}
          delay={1}
          delta={prevTotals && summary ? calcDelta(summary.totals.spend, prevTotals.spend) : null}
          invertDelta
          description="Total amount spent on Facebook Ads in the selected date range. Pulled live from the Meta API. % change compares against the equivalent prior period from your synced data."
          chartActive={activeChartMetrics.has("spend")}
          onChartToggle={() => toggleChartMetric("spend")}
          chartColor={CHART_METRIC_CONFIG.spend.color}
        />
        <HeroCard
          label="Impressions"
          value={m ? formatCompact(m.impressions) : "0"}
          subtitle={m ? formatNumber(m.impressions) : undefined}
          accentColor={getColor(1)}
          loading={reachLoading}
          delay={2}
          delta={prevTotals && summary ? calcDelta(summary.totals.impressions, prevTotals.impressions) : null}
          description="Total number of times your ads were displayed to users in the selected period. One person seeing the same ad 3 times = 3 impressions. Pulled live from the Meta API."
          chartActive={activeChartMetrics.has("impressions")}
          onChartToggle={() => toggleChartMetric("impressions")}
          chartColor={CHART_METRIC_CONFIG.impressions.color}
        />
        <HeroCard
          label="Reach"
          value={m ? formatCompact(m.reach) : "0"}
          subtitle={m ? `${(m.frequency ?? 0).toFixed(1)}x frequency` : undefined}
          accentColor={getColor(3)}
          loading={reachLoading}
          delay={3}
          delta={prevTotals && summary ? calcDelta(summary.totals.reach, prevTotals.reach) : null}
          description="Number of unique people who saw your ads at least once. Frequency = Impressions ÷ Reach — how many times the average person saw your ads. High frequency (>3-4x) can cause ad fatigue."
          chartActive={activeChartMetrics.has("reach")}
          onChartToggle={() => toggleChartMetric("reach")}
          chartColor={CHART_METRIC_CONFIG.reach.color}
        />
        <HeroCard
          label="Leads"
          value={m ? formatNumber(m.leads) : "0"}
          subtitle={m && m.leads > 0 ? `${formatCurrency(m.cpl)} per lead` : undefined}
          accentColor={getColor(2)}
          loading={reachLoading}
          delay={4}
          delta={prevTotals && summary ? calcDelta(summary.totals.leads, prevTotals.leads) : null}
          description="Total Facebook Lead form submissions in the selected period. CPL (Cost Per Lead) = Total Spend ÷ Total Leads. These are Facebook-native leads, not opt-ins tracked via your funnel."
          chartActive={activeChartMetrics.has("leads")}
          onChartToggle={() => toggleChartMetric("leads")}
          chartColor={CHART_METRIC_CONFIG.leads.color}
        />
      </div>

      {/* ── Secondary Metrics Strip ─────────────────────────────────── */}
      <div className="bg-card rounded-2xl border border-border">
        <div className="flex flex-wrap items-center justify-between divide-x divide-border/50">
          <CompactStat label="Link Clicks" value={m ? formatCompact(m.linkClicks) : "0"} loading={reachLoading} delay={0} description="Clicks specifically on links within your ads — excludes reactions, comments, page likes, and other non-link interactions." chartActive={activeChartMetrics.has("linkClicks")} onChartToggle={() => toggleChartMetric("linkClicks")} chartColor={CHART_METRIC_CONFIG.linkClicks.color} />
          <CompactStat label="CTR" value={m ? formatPercent(m.ctr) : "0%"} loading={reachLoading} delay={1} description="Click-Through Rate = Clicks ÷ Impressions × 100. How often people click your ad after seeing it. Industry average for Meta is ~0.9%. Above 2% is strong." chartActive={activeChartMetrics.has("ctr")} onChartToggle={() => toggleChartMetric("ctr")} chartColor={CHART_METRIC_CONFIG.ctr.color} />
          <CompactStat label="CPC" value={m ? formatCurrency(m.cpc) : "$0"} loading={reachLoading} delay={2} description="Cost Per Click = Total Spend ÷ Total Clicks. Every click on your ad costs this much on average." chartActive={activeChartMetrics.has("cpc")} onChartToggle={() => toggleChartMetric("cpc")} chartColor={CHART_METRIC_CONFIG.cpc.color} />
          <CompactStat label="CPM" value={m ? formatCurrency(m.cpm) : "$0"} loading={reachLoading} delay={3} description="Cost Per 1,000 Impressions = (Spend ÷ Impressions) × 1,000. How much you pay to show your ads 1,000 times. Reflects auction competition and audience quality." chartActive={activeChartMetrics.has("cpm")} onChartToggle={() => toggleChartMetric("cpm")} chartColor={CHART_METRIC_CONFIG.cpm.color} />
          <CompactStat label="CPL" value={m ? formatCurrency(m.cpl) : "$0"} loading={reachLoading} delay={4} description="Cost Per Lead = Total Spend ÷ Total Facebook Lead form submissions. Does not include opt-ins tracked separately through your funnel UTMs." chartActive={activeChartMetrics.has("cpl")} onChartToggle={() => toggleChartMetric("cpl")} chartColor={CHART_METRIC_CONFIG.cpl.color} />
          <CompactStat label="Frequency" value={m ? (m.frequency ?? 0).toFixed(2) : "0"} loading={reachLoading} delay={5} description="Average times each unique person saw your ads = Impressions ÷ Reach. Frequency above 3-4x in a short window often signals ad fatigue and declining performance." chartActive={activeChartMetrics.has("frequency")} onChartToggle={() => toggleChartMetric("frequency")} chartColor={CHART_METRIC_CONFIG.frequency.color} />
          <CompactStat label="Conv. Rate" value={m ? formatPercent(m.conversionRate) : "0%"} loading={reachLoading} delay={6} description="Conversion Rate = Leads ÷ Link Clicks × 100. The percentage of link clicks that resulted in a lead form submission. Uses link clicks (not total clicks) as the denominator." chartActive={activeChartMetrics.has("conversionRate")} onChartToggle={() => toggleChartMetric("conversionRate")} chartColor={CHART_METRIC_CONFIG.conversionRate.color} />
        </div>
      </div>

      {/* ── Charts ──────────────────────────────────────────────────── */}
      <SpendChart data={summary?.dailyData || []} loading={loading} activeMetrics={activeChartMetrics} />

      {/* ── Tab Bar ─────────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-card rounded-xl border border-border p-1 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.value
                ? "bg-[var(--brand-primary)] text-white shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ─────────────────────────────────────────────── */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === "performance" ? (
            <PerformanceTable
              data={summary?.rows || []}
              loading={loading}
              creativeRows={creativePerformance?.rows}
            />
          ) : (
            <DemographicsTable
              data={demographics}
              loading={demoLoading}
              overallRoas={creativePerformance?.summary.overallRoas}
              cashCollected={creativePerformance?.rows.reduce((s, r) => s + r.cashCollected, 0)}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default Ads;
